import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input, Label, FieldError } from '@/components/ui/Form';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';

type Mode = 'register' | 'set-password' | 'reset-password';

interface Props {
  mode: Mode;
}

const TITLES: Record<Mode, { title: string; sub: string; submit: string; nav: string }> = {
  register: {
    title: '注册观微',
    sub: '邮箱 + 验证码 + 密码三步完成',
    submit: '完成注册',
    nav: '/chart',
  },
  'set-password': {
    title: '设置密码',
    sub: '老用户首次设置登录密码',
    submit: '设置密码',
    nav: '/chart',
  },
  'reset-password': {
    title: '重置密码',
    sub: '通过邮箱验证码重置密码',
    submit: '重置密码',
    nav: '/chart',
  },
};

const PURPOSE_MAP: Record<Mode, 'REGISTER' | 'SET_PASSWORD' | 'RESET_PASSWORD'> = {
  register: 'REGISTER',
  'set-password': 'SET_PASSWORD',
  'reset-password': 'RESET_PASSWORD',
};

export function CodeFlowPage({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const meta = TITLES[mode];
  const sendCode = useAuthStore((s) => s.sendCode);
  const register = useAuthStore((s) => s.register);
  const setPassword = useAuthStore((s) => s.setPassword);
  const resetPassword = useAuthStore((s) => s.resetPassword);
  const loading = useAuthStore((s) => s.loading);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword2] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('请先填写邮箱');
      return;
    }
    setSending(true);
    try {
      await sendCode(email.trim().toLowerCase(), PURPOSE_MAP[mode]);
      setInfo('验证码已发送，请查收邮箱（有效期：五分钟）');
      setCountdown(60);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const e2 = email.trim().toLowerCase();
      if (mode === 'register') await register(e2, code, password);
      else if (mode === 'set-password') await setPassword(e2, code, password);
      else await resetPassword(e2, code, password);
      navigate(location.state?.from ?? meta.nav, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <h1 className="font-serif text-3xl text-fg text-center mb-2">{meta.title}</h1>
        <p className="text-sm text-fg-muted text-center mb-8">{meta.sub}</p>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" required>
                邮箱
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div>
              <Label htmlFor="code" required>
                验证码
              </Label>
              <div className="flex gap-3">
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="6 位"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={sending || countdown > 0}
                  className="shrink-0"
                >
                  {countdown > 0 ? `${countdown}s` : sending ? '发送中…' : '获取验证码'}
                </Button>
              </div>
              {info && <p className="mt-1 text-xs text-success">{info}</p>}
            </div>

            <div>
              <Label htmlFor="password" required>
                {mode === 'reset-password' ? '新密码' : '密码'}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword2(e.target.value)}
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                required
              />
              <p className="mt-1 text-xs text-fg-muted">8 位以上，建议包含字母与数字</p>
            </div>

            <FieldError>{error}</FieldError>

            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? '处理中…' : meta.submit}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-fg-2">
            已有账号？
            <Link
              to="/auth/login"
              state={{ from: location.state?.from }}
              className="ml-1 text-accent hover:text-accent-hover"
            >
              去登录
            </Link>
          </div>
        </Card>
      </motion.div>
    </main>
  );
}
